export function getBackgroundColor(backgroundColor: string): string {
  if (!backgroundColor) {
    return '';
  }

  // White pill for all statuses — colored border + label (matches production list grids).
  return '#FFFFFF';
}

export function getRandomDarkColor() {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  return `rgb(${r}, ${g}, ${b})`;
}


export let colorList= [
  "#FF6633", "#B34D4D", "#80B300", "#809900", "#6680B3", "#66991A", "#E6331A", "#4D8000", 
  "#B33300", "#4D8066", "#809980", "#FF3380", "#4D80CC", "#9900B3", "#E64D66", "#4DB380", 
  "#FF4D4D", "#6666FF", "#B3CC33", "#8A2BE2", "#DC143C", "#8B4513", "#6495ED", "#006400", 
  "#8B008B", "#DA70D6", "#7B68EE", "#4682B4", "#D2691E", "#FF8C00", "#20B2AA", "#008B8B", 
  "#A52A2A", "#5F9EA0", "#9932CC", "#B22222", "#556B2F", "#B8860B", "#A0522D", "#808000", 
  "#DB7093", "#FF4500", "#A0522D", "#9932CC", "#FF6347", "#8B0000", "#8FBC8F", "#483D8B"
];


export function formatDate(date: Date): string {
  const year = date.getFullYear().toString(); // Full 4-digit year
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Ensure 2 digits for month
  const day = date.getDate().toString().padStart(2, '0'); // Ensure 2 digits for day
  return `${year}-${month}-${day}`;
}

export function getRandomLightColor() {
  const r = Math.floor(Math.random() * 60) + 195; // Range: 195–255 for red (lighter range)
  const g = Math.floor(Math.random() * 60) + 195; // Range: 195–255 for green (lighter range)
  const b = Math.floor(Math.random() * 60) + 195; // Range: 195–255 for blue (lighter range)

  // Optionally, ensure more variation by ensuring the channels are sufficiently different.
  // This can be done by adjusting the values to have random but noticeable differences.

  // A slight shift to ensure color components are sufficiently different
  if (Math.random() < 0.5) {
    const shift = Math.floor(Math.random() * 35); // Random shift for more distinctness
    return `rgb(${r - shift}, ${g + shift}, ${b})`; // Adds some distinctness between RGB channels
  }

  return `rgb(${r}, ${g}, ${b})`; 
}

export function getLargestTimeUnit(startDate: Date, endDate: Date): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());

  const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const years = Math.floor(days / 365);
  const months = Math.floor(days / 30); // Approximate month length
  const weeks = Math.floor(days / 7);

  if (years > 0) return "years";
  if (months > 0) return "months";
  if (weeks > 0) return "weeks";
  return "days";
}

export function getTriggerTextColor(bgColor: string): string {
  return getAccessibleBadgeStyles(bgColor || '#66bb6a').color;
}

/**
 * Rewrites a PDF template's relative logo paths so they resolve when the
 * template html is rendered in the app preview.
 *
 * Shared home for this transform. The replacement strings are kept byte for
 * byte identical to the copy held privately by the Settings -> PDF Template
 * screen, so both previews render the same; that screen keeps its own copy
 * until it is next touched, since it must not be modified here.
 */
export function transformPdfImagePaths(content: string): string {
  if (!content) return '';

  // Upper logo image
  content = content.replace(
    /src="images\/logo\.jpg"/g,
    'src="../../../../assets/img/logo.jpg"'
  );

  // Lower logo image which is in center
  content = content.replace(
    /src="images\/center-logo\.jpg"/g,
    'src="../../../../assets/img/center-logo.jpg"'
  );

  return content;
}

export function getAccessibleBadgeStyles(backgroundColor: string): {
  backgroundColor: string;
  color: string;
  borderColor: string;
} {
  const fallback = {
    backgroundColor: '#6b7280',
    color: '#ffffff',
    borderColor: '#4b5563',
  };
  if (!backgroundColor) return fallback;

  const parseRgb = (color: string): { r: number; g: number; b: number } | null => {
    const c = color.trim();
    if (c.startsWith('#')) {
      const hex = c.slice(1);
      const full = hex.length === 3 ? hex.split('').map((x) => x + x).join('') : hex;
      if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
      return {
        r: parseInt(full.slice(0, 2), 16),
        g: parseInt(full.slice(2, 4), 16),
        b: parseInt(full.slice(4, 6), 16),
      };
    }
    if (c.startsWith('rgb')) {
      const v = c.match(/\d+(\.\d+)?/g);
      if (!v || v.length < 3) return null;
      return { r: Number(v[0]), g: Number(v[1]), b: Number(v[2]) };
    }
    return null;
  };

  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

  const luminance = (r: number, g: number, b: number) => {
    const t = [r, g, b].map((v) => {
      const s = v / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * t[0] + 0.7152 * t[1] + 0.0722 * t[2];
  };

  const contrast = (
    a: { r: number; g: number; b: number },
    b: { r: number; g: number; b: number }
  ) => {
    const l1 = luminance(a.r, a.g, a.b);
    const l2 = luminance(b.r, b.g, b.b);
    const [maxL, minL] = l1 > l2 ? [l1, l2] : [l2, l1];
    return (maxL + 0.05) / (minL + 0.05);
  };

  const rgb = parseRgb(backgroundColor);
  if (!rgb) return { ...fallback, backgroundColor };

  // Enforce minimum contrast by darkening light/pastel backgrounds.
  let bg = { ...rgb };
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 17, g: 24, b: 39 };
  for (let i = 0; i < 8; i++) {
    const bestText = contrast(bg, black) >= contrast(bg, white) ? black : white;
    if (contrast(bg, bestText) >= 4.5) break;
    bg = { r: bg.r * 0.88, g: bg.g * 0.88, b: bg.b * 0.88 };
  }

  const text = contrast(bg, black) >= contrast(bg, white) ? black : white;

  // A white/near-white status colour (e.g. a "Completed" status configured
  // as #ffffff) passes the text-contrast loop above untouched — black text
  // on white is 21:1 — but the fill itself then reads as invisible against
  // a white page: no shape, just floating text. `border` is normally just
  // a darkened tint of the fill (bg * 0.68), which for white comes out a
  // pale, barely-there grey. Give it a solid dark outline instead whenever
  // the fill can't be told apart from white, so the pill still has a shape.
  const border = contrast(bg, white) < 1.3
    ? black
    : { r: bg.r * 0.68, g: bg.g * 0.68, b: bg.b * 0.68 };

  return {
    backgroundColor: `#${toHex(bg.r)}${toHex(bg.g)}${toHex(bg.b)}`,
    color: text === black ? '#111827' : '#ffffff',
    borderColor: `#${toHex(border.r)}${toHex(border.g)}${toHex(border.b)}`,
  };
}

/**
 * Readable version of a status colour for use as TEXT on the white list pills.
 *
 * The list grids render a white pill with a coloured border and coloured label
 * (see {@link getBackgroundColor}), so the label must contrast against WHITE.
 * `getAccessibleBadgeStyles` solves the opposite case — text drawn ON the status
 * colour, as the detail pills do — and returns a flat black or white, which on a
 * white pill would make dark statuses invisible.
 *
 * Same principle as the detail pill, adapted to a white background: darken the
 * status colour step by step until it clears the WCAG AA 4.5:1 ratio against
 * white. Pale statuses (e.g. ADDENDUM / RFI #BDD6EE) become dark and legible
 * while keeping their hue; already-readable colours (LOST BID, ACCEPTED) are
 * returned untouched.
 */
export function getReadableTextOnWhite(statusColor: string): string {
  const fallback = '#111827';
  if (!statusColor) return fallback;

  const c = statusColor.trim();
  let rgb: { r: number; g: number; b: number } | null = null;

  if (c.startsWith('#')) {
    const hex = c.slice(1);
    const full = hex.length === 3 ? hex.split('').map((x) => x + x).join('') : hex;
    if (/^[0-9a-fA-F]{6}$/.test(full)) {
      rgb = {
        r: parseInt(full.slice(0, 2), 16),
        g: parseInt(full.slice(2, 4), 16),
        b: parseInt(full.slice(4, 6), 16),
      };
    }
  } else if (c.startsWith('rgb')) {
    const v = c.match(/\d+(\.\d+)?/g);
    if (v && v.length >= 3) {
      rgb = { r: Number(v[0]), g: Number(v[1]), b: Number(v[2]) };
    }
  }

  if (!rgb) return fallback;

  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

  const luminance = (r: number, g: number, b: number) => {
    const t = [r, g, b].map((v) => {
      const s = v / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * t[0] + 0.7152 * t[1] + 0.0722 * t[2];
  };

  // Contrast of the candidate text colour against pure white.
  const contrastWithWhite = (col: { r: number; g: number; b: number }) =>
    1.05 / (luminance(col.r, col.g, col.b) + 0.05);

  // Target AAA (7:1) rather than AA (4.5:1). Pill labels are small and sit on a
  // near-white tint, and stopping at 4.5 left them visibly washed out next to the
  // solid detail-page badge — e.g. #FF2B2B stopped at #E02626 (~4.7:1). 7:1 keeps
  // the hue recognisable while matching the darker weight the detail header reads at.
  let text = { ...rgb };
  for (let i = 0; i < 20 && contrastWithWhite(text) < 7; i++) {
    text = { r: text.r * 0.88, g: text.g * 0.88, b: text.b * 0.88 };
  }

  return `#${toHex(text.r)}${toHex(text.g)}${toHex(text.b)}`;
}

/**
 * Resolves a user's profile image to a displayable URL string.
 *
 * `profile_image` comes back from the API either as a plain string URL, or
 * (via AttachFileResource) as an object `{ url, thumbnail, ... }`. Binding
 * that object directly as an <img> src fails silently, so every avatar
 * caller needs to unwrap it the same way. Prefers `.url` over `.thumbnail`
 * since `.thumbnail` can be an empty string when no thumbnail was generated.
 */
export function resolveProfileImageUrl(
  value: string | { url?: string; thumbnail?: string } | null | undefined
): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.url || value.thumbnail || '';
}

/**
 * Short display form of a long name: the first `maxWords` words followed by "…".
 * Names with `maxWords` words or fewer are returned unchanged (the full text is
 * meant to stay available through a tooltip).
 */
export function shortenName(name: string | null | undefined, maxWords = 2): string {
  const text = String(name ?? '').trim();
  if (!text) { return ''; }
  const words = text.split(/\s+/);
  return words.length > maxWords ? words.slice(0, maxWords).join(' ') + '…' : text;
}

/**
 * Detail page of the record a task, ticket or note was created under, opened on that page's Tasks, Tickets or Notes tab.
 * Keyed by the backend module id (same catalog as MODULE_IDS); `tasks` / `tickets` are the positions of those tabs
 * on that detail page (a module without that tab opens on its first tab). Empty when the module has no detail page.
 */
export function referenceRoute(moduleId: number | undefined, recordId: number | undefined, tab: 'tasks' | 'tickets' | 'notes'): string {
  if (!moduleId || !recordId) return '';
  const routes: Record<number, { path: string; tasks?: number; tickets?: number; notes?: number }> = {
    16: { path: 'bids/bid', tasks: 7, notes: 8 },
    19: { path: 'jobs/detail', tasks: 10, tickets: 11, notes: 12 },
    29: { path: 'jobsites/jobsite', tasks: 8, tickets: 9, notes: 10 },
    13: { path: 'customers/customer', tasks: 7, tickets: 6, notes: 5 },
    21: { path: 'subcontractors/subcontractor', tasks: 5, tickets: 4, notes: 6 },
    12: { path: 'vendors/vendor', tasks: 8, tickets: 6, notes: 7 },
    17: { path: 'warehouses/warehouse', tasks: 6, tickets: 5, notes: 4 },
    10: { path: 'products/product', tasks: 6, tickets: 5, notes: 4 },
    18: { path: 'services/service', tasks: 5, tickets: 3, notes: 4 },
    11: { path: 'hr/staff', tasks: 6, tickets: 5, notes: 9 },
  };
  const target = routes[moduleId];
  if (!target) return '';
  const position = target[tab];
  return position === undefined ? `/${target.path}/${recordId}` : `/${target.path}/${recordId}?tab=${position}`;
}

/** Detail page of a task's reference record, opened on its Tasks tab. */
export function taskReferenceRoute(moduleId: number | undefined, recordId: number | undefined): string {
  return referenceRoute(moduleId, recordId, 'tasks');
}

/** Detail page of a note's reference record, opened on its Notes tab. */
export function noteReferenceRoute(moduleId: number | undefined, recordId: number | undefined): string {
  return referenceRoute(moduleId, recordId, 'notes');
}

/** Detail page of a ticket's reference record, opened on its Tickets tab. */
export function ticketReferenceRoute(moduleId: number | undefined, recordId: number | undefined): string {
  return referenceRoute(moduleId, recordId, 'tickets');
}

/** Singular module name for a backend module id, for labelling a task's reference record ("Bid", "Job", ...). */
export function taskReferenceModuleLabel(moduleId: number | undefined): string {
  const labels: Record<number, string> = {
    16: 'Bid', 19: 'Job', 29: 'Jobsite', 13: 'Customer', 21: 'Subcontractor',
    12: 'Vendor', 17: 'Warehouse', 10: 'Product', 18: 'Service', 11: 'Staff',
  };
  return moduleId ? labels[moduleId] ?? '' : '';
}
